import { useState, useEffect, useRef } from 'react';
import { Phone, Menu, X, User, ChevronDown } from 'lucide-react';

import { apiService } from '../services/api';
import { getStaticData } from '../hooks/useStaticData';


import { Button } from './ui/button';
// import ContactModal from './ContactModal'; // TODO: re-enable if contact form returns

import { CDN_URL } from '@/config/cdn';

// Custom hover dropdown for desktop navigation
const HoverDropdown = ({ link, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    // Small delay before closing to allow moving to dropdown
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <a
        href={link.url}
        className="flex items-center gap-1 text-gray-700 hover:text-red-600 font-medium transition-colors"
      >
        {link.title}
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </a>
      {isOpen && (
        <div className="absolute top-full left-0 pt-1 z-50">
          <div className="min-w-[160px] rounded-md border bg-white p-1 shadow-md">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedMobileLinks, setExpandedMobileLinks] = useState({});
  // Use static data as initial state to avoid flash of default content
  const [businessInfo, setBusinessInfo] = useState(() => getStaticData('businessInfo'));
  const [navLinks, setNavLinks] = useState(() => getStaticData('navigationLinks') || []);
  const [isCustomerLoggedIn, setIsCustomerLoggedIn] = useState(false);
  // Check if customer is logged in
  useEffect(() => {
    const token = localStorage.getItem('customerToken');
    setIsCustomerLoggedIn(!!token);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const [info, links] = await Promise.all([
        apiService.getBusinessInfo(),
        apiService.getNavigationLinks(true) // Fetch nested structure
      ]);
      if (info) setBusinessInfo(info);
      if (links) setNavLinks(links);
    };
    fetchData();
  }, []);
  
  const handleCall = () => {
    window.location.href = 'tel:+13524280086';
  };

  const handleBooking = () => {
    window.location.href = 'tel:+13524280086';
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo - Clickable to home */}
            <a href="/" className="flex items-center space-x-2 md:space-x-3 hover:opacity-80 transition-opacity">
              <img 
                src={`${CDN_URL}/images/owaza-logo.jpg`} 
                alt="Owaza Martial Arts" 
                className="h-10 w-10 md:h-12 md:w-12 object-contain rounded-full"
              />
              <div>
                <h1 className="text-base md:text-xl font-bold text-gray-900">Owaza Martial Arts</h1>
                <p className="text-xs md:text-sm text-gray-600 hidden xl:block">Spring Hill, FL</p>
              </div>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center space-x-8">
              {navLinks
                .filter(link => link.show_in_header && !link.parent_id)
                .sort((a, b) => a.order - b.order)
                .map(link => {
                  const hasChildren = link.children && link.children.length > 0;

                  if (hasChildren) {
                    return (
                      <HoverDropdown key={link.id} link={link}>
                        {link.children
                          .filter(child => child.show_in_header)
                          .sort((a, b) => a.order - b.order)
                          .map(child => (
                            <a
                              key={child.id}
                              href={child.url}
                              className="block w-full rounded-sm px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-red-600 transition-colors"
                            >
                              {child.title}
                            </a>
                          ))
                        }
                      </HoverDropdown>
                    );
                  }

                  return (
                    <a
                      key={link.id}
                      href={link.url}
                      className="text-gray-700 hover:text-red-600 font-medium transition-colors"
                    >
                      {link.title}
                    </a>
                  );
                })
              }
            </nav>

            {/* Phone & CTA - Desktop */}
            <div className="hidden xl:flex items-center space-x-3">
              <a
                href="https://members.owazamartialarts.com"
                className="flex items-center space-x-1 text-gray-600 hover:text-red-600 transition-colors"
              >
                <User className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {isCustomerLoggedIn ? 'My Account' : 'Sign In'}
                </span>
              </a>

              <Button
                variant="outline"
                size="sm"
                className="flex items-center space-x-2 border-gray-300"
                onClick={handleCall}
              >
                <Phone className="h-4 w-4" />
                <span>{businessInfo?.phone || '(352) 428-0086'}</span>
              </Button>

              <Button
                onClick={handleBooking}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white font-semibold whitespace-nowrap"
              >
                Book Your Appointment
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              className="xl:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="xl:hidden bg-white border-t shadow-lg">
            <nav className="px-4 py-4 space-y-2">
              {navLinks
                .filter(link => link.show_in_header && !link.parent_id)
                .sort((a, b) => a.order - b.order)
                .map(link => {
                  const hasChildren = link.children && link.children.length > 0;
                  const isExpanded = expandedMobileLinks[link.id];

                  return (
                    <div key={link.id}>
                      {hasChildren ? (
                        <>
                          <div className="flex items-center justify-between">
                            <a
                              href={link.url}
                              className="flex-1 text-gray-700 hover:text-red-600 font-medium py-2"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              {link.title}
                            </a>
                            <button
                              onClick={() => setExpandedMobileLinks(prev => ({
                                ...prev,
                                [link.id]: !prev[link.id]
                              }))}
                              className="p-2 hover:bg-gray-100 rounded"
                            >
                              <ChevronDown
                                className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              />
                            </button>
                          </div>
                          {isExpanded && (
                            <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-3">
                              {link.children
                                .filter(child => child.show_in_header)
                                .sort((a, b) => a.order - b.order)
                                .map(child => (
                                  <a
                                    key={child.id}
                                    href={child.url}
                                    className="block text-sm text-gray-600 hover:text-red-600 py-1.5"
                                    onClick={() => setIsMenuOpen(false)}
                                  >
                                    {child.title}
                                  </a>
                                ))
                              }
                            </div>
                          )}
                        </>
                      ) : (
                        <a
                          href={link.url}
                          className="block text-gray-700 hover:text-red-600 font-medium py-2"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {link.title}
                        </a>
                      )}
                    </div>
                  );
                })
              }
              <div className="pt-4 space-y-3 border-t">
                <a
                  href="https://members.owazamartialarts.com"
                  className="w-full flex items-center justify-center space-x-2 py-2 text-gray-700 hover:text-red-600 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <User className="h-4 w-4" />
                  <span>{isCustomerLoggedIn ? 'My Account' : 'Member Sign In'}</span>
                </a>
                <Button
                  variant="outline"
                  className="w-full flex items-center justify-center space-x-2"
                  onClick={handleCall}
                >
                  <Phone className="h-4 w-4" />
                  <span>{businessInfo?.phone || '(352) 428-0086'}</span>
                </Button>
                <Button
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
                  onClick={handleBooking}
                >
                  Book Your Appointment
                </Button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Mobile Sticky Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-40 safe-area-pb">
        <div className="flex">
          <Button 
            variant="outline" 
            className="flex-1 rounded-none border-0 border-r py-4"
            onClick={handleBooking}
          >
            Book
          </Button>
          <Button 
            variant="outline" 
            className="flex-1 rounded-none border-0 border-r py-4"
            onClick={handleCall}
          >
            Call
          </Button>
          <Button 
            variant="outline" 
            className="flex-1 rounded-none border-0 py-4"
            onClick={() => window.location.href = `sms:${businessInfo?.phone?.replace(/[^0-9+]/g, '') || '+13524280086'}`}
          >
            Text
          </Button>
        </div>
      </div>

      {/* ContactModal removed — all CTAs now call Jim Rivera's direct line tel:+13524280086 */}
      {/* TODO: re-enable <ContactModal> here if contact form returns */}
    </>
  );
};

export default Header;